import { Checkbox, SimpleGrid } from '@mantine/core';
import { AppliedFilter, Filter } from '@uag/react-core';
import { RequestStateType, RequestType } from '@uag/welcome-shared/src/api/models';
import { useGetFilterableBranches } from '@uag/welcome-shared/src/api/queries/branches/branches';
import { useGetContributors } from '@uag/welcome-shared/src/api/queries/contributors/contributors';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { managementInstance } from 'i18n';
import { BranchCombobox } from 'shared';
import { getContributorOption } from 'shared/components/ContributorSelectOption';
import { ResponsiblePersonCombobox } from 'shared/components/ResponsiblePersonCombobox';
import { branchFilterAtom, requestTypeFilterAtom, responsiblePersonFilterAtom, stateFilterAtom } from '../RequestAtoms';
import { AppliedFiltersType, getDefaultAppliedFilters } from './AppliedFiltersType';

const excludedStates: Set<RequestStateType> = new Set([
    RequestStateType.AdditionalDataInput,
    RequestStateType.ServiceActivation,
]);

const updateSelection = <T extends string>(current: T[] | null, value: T, checked: boolean): T[] | null => {
    if (checked) {
        return current ? [...current, value] : [value];
    }

    return current?.filter((item) => item !== value) || null;
};

const getAppliedFilterTags = <T extends string>(values: T[] | null, title: string, getLabel: (value: T) => string) => {
    return (
        values?.map((value) => ({
            key: value,
            value: getLabel(value),
            title,
        })) ?? []
    );
};

const getAppliedFilterTag = <T extends string>(value: T | null, title: string, getLabel: (value: T) => string) => {
    return value ? [{ key: value, value: getLabel(value), title }] : [];
};

type Props = {
    searchText: string;
    setSearchText: (value: string) => void;
    appliedFilters: AppliedFiltersType;
    setAppliedFilters: (value: AppliedFiltersType) => void;
};

export const InboxFilter = ({ searchText, setSearchText, appliedFilters, setAppliedFilters }: Props) => {
    const { t } = useTranslation();
    const { data: apiBranches } = useGetFilterableBranches();
    const { data: apiContributors } = useGetContributors();
    const [stateFilter, setStateFilter] = useAtom(stateFilterAtom);
    const [requestTypeFilter, setRequestTypeFilter] = useAtom(requestTypeFilterAtom);
    const [branchFilter, setBranchFilter] = useAtom(branchFilterAtom);
    const [responsiblePersonFilter, setResponsiblePersonFilter] = useAtom(responsiblePersonFilterAtom);

    const handleStateFilterChange = (stateValue: RequestStateType, checked: boolean) => {
        setStateFilter((prev) => updateSelection(prev, stateValue, checked));
    };

    const handleRequestTypeFilterChange = (requestTypeValue: RequestType, checked: boolean) => {
        setRequestTypeFilter((prev) => updateSelection(prev, requestTypeValue, checked));
    };

    const handleClearFilters = () => {
        setStateFilter(null);
        setRequestTypeFilter(null);
        setBranchFilter(null);
        setResponsiblePersonFilter(null);
        setAppliedFilters(getDefaultAppliedFilters());
    };

    const handleFilterModalClosed = (applyFilters: boolean) => {
        if (applyFilters) {
            applyFilterSelections();
            return;
        }

        revertFilterSelections();
    };

    const applyFilterSelections = () => {
        setAppliedFilters({
            state: stateFilter,
            requestType: requestTypeFilter,
            branch: branchFilter,
            responsiblePerson: responsiblePersonFilter,
        });
    };

    const revertFilterSelections = () => {
        setStateFilter(appliedFilters.state);
        setRequestTypeFilter(appliedFilters.requestType);
        setBranchFilter(appliedFilters.branch);
        setResponsiblePersonFilter(appliedFilters.responsiblePerson);
    };

    const getStateFilterValues = () => {
        return Object.values(RequestStateType)
            .filter((state) => !excludedStates.has(state))
            .map((state) => ({
                value: state,
                label: managementInstance.t(`RequestState.${state}`),
            }));
    };

    const getRequestTypeFilterValues = () => {
        return Object.values(RequestType).map((requestType) => ({
            value: requestType,
            label: managementInstance.t(`requestTypes.${requestType}`),
        }));
    };

    const handleClearAppliedFilter = (filter: AppliedFilter) => {
        const updated = { ...appliedFilters };
        switch (filter.key) {
            case 'branchFilter':
                updated.branch = null;
                setBranchFilter(null);
                break;
            case 'responsiblePersonFilter':
                updated.responsiblePerson = null;
                setResponsiblePersonFilter(null);
                break;
            case RequestType.Doka360:
            case RequestType.NonDoka360:
                updated.requestType = updateSelection(updated.requestType, filter.key as RequestType, false);
                setRequestTypeFilter(updated.requestType);
                break;
            default:
                updated.state = updateSelection(updated.state, filter.key as RequestStateType, false);
                setStateFilter(updated.state);
        }
        setAppliedFilters(updated);
    };

    const branches = useMemo(
        () => apiBranches?.data?.map((branch) => ({ value: branch.id, label: branch.name })) ?? [],
        [apiBranches],
    );

    const contributors = useMemo(() => apiContributors?.data?.map(getContributorOption) ?? [], [apiContributors]);

    const appliedFilterTags = useMemo(() => {
        return [
            ...getAppliedFilterTag(
                appliedFilters.branch,
                t('branch'),
                (branch) => branches.find((item) => item.value === branch)?.label ?? '',
            ),
            ...getAppliedFilterTag(
                appliedFilters.responsiblePerson,
                t('responsiblePerson'),
                (responsiblePerson) => contributors.find((item) => item.value === responsiblePerson)?.displayName ?? '',
            ),
            ...getAppliedFilterTags(appliedFilters.requestType, t('requestType'), (requestType) =>
                t(`requestTypes.${requestType}`),
            ),
            ...getAppliedFilterTags(appliedFilters.state, '', (state) => t(`RequestState.${state}`)),
        ];
    }, [appliedFilters, branches, contributors, t]);

    return (
        <Filter
            appliedFilters={appliedFilterTags}
            filterButtonType="icon-only"
            searchText={searchText}
            searchTextMaxLength={50}
            onClearAppliedFilter={handleClearAppliedFilter}
            onClearFilters={handleClearFilters}
            onFilterDrawerClose={handleFilterModalClosed}
            onSearchTextChanged={setSearchText}
        >
            <Filter.Filters>
                <BranchCombobox
                    key="branchFilter"
                    value={branchFilter}
                    onChange={(value) => setBranchFilter(value ?? null)}
                />
                <ResponsiblePersonCombobox
                    key="responsiblePersonFilter"
                    value={responsiblePersonFilter}
                    onChange={(value) => setResponsiblePersonFilter(value ?? null)}
                />
                <SimpleGrid>
                    <p className="py-1 text-base">{t('requestState')}</p>
                    {getStateFilterValues().map((state) => (
                        <Checkbox
                            key={state.value}
                            checked={stateFilter?.includes(state.value) || false}
                            label={state.label}
                            onChange={(e) => handleStateFilterChange(state.value, e.target.checked)}
                        />
                    ))}
                </SimpleGrid>
                <SimpleGrid>
                    <p className="py-1 text-base">{t('requestType')}</p>
                    {getRequestTypeFilterValues().map((requestType) => (
                        <Checkbox
                            key={requestType.value}
                            checked={requestTypeFilter?.includes(requestType.value) || false}
                            label={requestType.label}
                            onChange={(e) => handleRequestTypeFilterChange(requestType.value, e.target.checked)}
                        />
                    ))}
                </SimpleGrid>
            </Filter.Filters>
        </Filter>
    );
};
